import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

/**
 * Reference-counts open scroll-lock <details> elements so overlapping or stale
 * dialogs can't leak the documentElement `scroll-lock` attribute (which maps to
 * html { overflow: hidden } and would otherwise freeze window scrolling).
 * @type {Set<HTMLDetailsElement>}
 */
const openScrollLockDetails = new Set();

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        if (event.target.open) {
          openScrollLockDetails.add(event.target);
        } else {
          openScrollLockDetails.delete(event.target);
        }

        document.documentElement.toggleAttribute('scroll-lock', openScrollLockDetails.size > 0);
      }
    }
  },
  { capture: true }
);

/**
 * Clears any leaked `scroll-lock` if no scroll-lock <details> is actually open.
 * Runs once on initial load and on bfcache restores (pageshow).
 */
const clearLeakedScrollLock = () => {
  const anyOpen = document.querySelector('details[scroll-lock][open]');
  if (!anyOpen) {
    openScrollLockDetails.clear();
    document.documentElement.removeAttribute('scroll-lock');
  }
};

window.addEventListener('pageshow', clearLeakedScrollLock);
document.addEventListener('DOMContentLoaded', clearLeakedScrollLock);

/**
 * BLANK / FROZEN PAGE WHEN THE SHOPPER PRESSES BACK FROM CHECKOUT — FIX (14 Jul 2026).
 *
 * TWO separate faults combined to make the page unusable on Back, which stopped shoppers
 * returning to add more parts:
 *
 *   1. `layout/theme.liquid` adds the class `cd-leaving` to <html> when CHECK OUT is tapped
 *      (a fade-out transition). Its CSS sets `body { opacity: 0; filter: blur(4px);
 *      pointer-events: none }` plus a full-screen white overlay. NOTHING ever removes the
 *      class, so when the browser restores the page from its back/forward cache the class is
 *      still there — the page renders fully but is INVISIBLE and unclickable. That is the
 *      blank white screen.
 *
 *   2. `showDialog()` above locks the page while the cart drawer is open with
 *      `body { position: fixed; top: -Ypx }`. Only `closeDialog()` clears it, and leaving via
 *      the CHECK OUT link never calls it — so the restored page is also scroll-locked.
 *
 * This clears both on every restore/back, closes any dialog left open, and returns the
 * shopper to the scroll position they left from.
 */
const cdUnfreezePage = () => {
  document.documentElement.classList.remove('cd-leaving');
  document.documentElement.removeAttribute('scroll-lock');

  for (const dialog of document.querySelectorAll('dialog[open]')) {
    try {
      dialog.close();
    } catch (error) {
      /* already closed */
    }
    dialog.classList.remove('dialog-closing');
  }

  const top = document.body.style.top;
  const wasLocked = document.body.style.position === 'fixed';

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';

  if (wasLocked) {
    const scrollY = Math.abs(parseInt(top || '0', 10)) || 0;
    window.scrollTo({ top: scrollY, behavior: 'instant' });
  }
};

window.addEventListener('pageshow', cdUnfreezePage);
window.addEventListener('popstate', cdUnfreezePage);
// Belt and braces: drop the fade-out class before the page is frozen into the bfcache.
window.addEventListener('pagehide', () => {
  document.documentElement.classList.remove('cd-leaving');
});
